/**
 * City SEO data — top 4 Texas markets where Velcor offers service.
 * Each city has bilingual (es/en) content + TDU info + ZIP codes for SEO.
 *
 * Slug routing:
 *   ES: /electricidad-{slug}
 *   EN: /electricity-{slug}
 */

export const CITIES = {
  houston: {
    slug: "houston",
    name: "Houston",
    state: "TX",
    population: "2.3M",
    tdu: { name: "CenterPoint Energy", phone: "(800) 332-7143" },
    avgRate: 11.4,
    avgBill: 132,
    zips: [
      "77001", "77002", "77003", "77004", "77005", "77006", "77007", "77008",
      "77009", "77010", "77011", "77012", "77013", "77014", "77015", "77016",
      "77017", "77018", "77019", "77020", "77021", "77022", "77023", "77024",
      "77025", "77026", "77027", "77028", "77029", "77030", "77031", "77032",
      "77033", "77034", "77035", "77036", "77040", "77041", "77042", "77043",
      "77044", "77045", "77047", "77048", "77049", "77050", "77051", "77053",
      "77054", "77056", "77057", "77058", "77059", "77060", "77061", "77062",
      "77063", "77064", "77065", "77066", "77067", "77068", "77069", "77070",
      "77071", "77072", "77073", "77074", "77075", "77076", "77077", "77078",
      "77079", "77080", "77081", "77082", "77083", "77084", "77085", "77086",
      "77087", "77088", "77089", "77090", "77091", "77092", "77093", "77094",
      "77095", "77096", "77098", "77099",
    ],
    neighborhoods: [
      "Downtown Houston", "Midtown", "Montrose", "The Heights", "Memorial",
      "Westchase", "Galleria / Uptown", "Spring Branch", "Sharpstown",
      "East End", "Third Ward", "Pearland", "Sugar Land", "Katy", "Cypress",
    ],
    es: {
      heroKicker: "Electricidad en Houston",
      heroTitle: "Houston merece luz sin sorpresas.",
      heroSub: "Tarifa fija, atención bilingüe y activación gratis en cualquier ZIP de CenterPoint Energy.",
      whyTitle: "¿Por qué Houston elige Velcor?",
      whyBody: "Más de 2.3 millones de hogares dependen de CenterPoint Energy como su distribuidora (TDU). Nosotros somos tu Proveedor Minorista de Electricidad (REP) — el que decide cuánto pagas. En Velcor te damos un plan de tarifa fija, sin contratos a largo plazo, y soporte 100% en español.",
      faqs: [
        { q: "¿Velcor da servicio en mi ZIP de Houston?", a: "Sí — damos servicio en todos los códigos postales 770XX y 772XX cubiertos por CenterPoint. Solo verifica tu dirección al inscribirte." },
        { q: "¿Cuánto se paga de luz en Houston?", a: "El consumo promedio en Houston es ~1,150 kWh/mes con factura ~$132. Con Velcor, tu tarifa queda fija desde el día 30 — sin variaciones por temporada." },
        { q: "¿CenterPoint cobra aparte?", a: "No. Los cargos de CenterPoint (TDU) ya están incluidos en tu factura mensual de Velcor. Una sola factura, una sola línea de atención." },
        { q: "¿Hay depósito o fee de activación?", a: "Cero. Activación gratis, sin depósito de seguridad, sin contrato a largo plazo. Cancela cuando quieras." },
      ],
    },
    en: {
      heroKicker: "Houston Electricity",
      heroTitle: "Houston deserves predictable power.",
      heroSub: "Fixed-rate plans, bilingual support and free activation across every CenterPoint Energy ZIP.",
      whyTitle: "Why Houston picks Velcor",
      whyBody: "More than 2.3 million households rely on CenterPoint Energy as their utility (TDU). We are your Retail Electric Provider (REP) — the one who actually sets your price. With Velcor you get a fixed-rate plan, no long-term contract, and 100% bilingual support.",
      faqs: [
        { q: "Does Velcor serve my Houston ZIP?", a: "Yes — we serve every 770XX and 772XX ZIP code covered by CenterPoint. Just verify your address at signup." },
        { q: "How much does electricity cost in Houston?", a: "The average Houston home uses ~1,150 kWh/month for ~$132. With Velcor your rate is locked from day 30 — no seasonal swings." },
        { q: "Does CenterPoint bill separately?", a: "No. CenterPoint (TDU) charges are already included in your monthly Velcor bill. One bill, one support line." },
        { q: "Is there a deposit or activation fee?", a: "Zero. Free activation, no security deposit, no long-term contract. Cancel any time." },
      ],
    },
  },

  dallas: {
    slug: "dallas",
    name: "Dallas",
    state: "TX",
    population: "1.3M",
    tdu: { name: "Oncor Electric Delivery", phone: "(888) 313-4747" },
    avgRate: 11.8,
    avgBill: 128,
    zips: [
      "75201", "75202", "75203", "75204", "75205", "75206", "75207", "75208",
      "75209", "75210", "75211", "75212", "75214", "75215", "75216", "75217",
      "75218", "75219", "75220", "75221", "75223", "75224", "75225", "75226",
      "75227", "75228", "75229", "75230", "75231", "75232", "75233", "75234",
      "75235", "75236", "75237", "75238", "75240", "75241", "75243", "75244",
      "75246", "75247", "75248", "75249", "75250", "75251", "75252", "75253",
      "75254", "75270", "75287",
    ],
    neighborhoods: [
      "Downtown Dallas", "Uptown", "Deep Ellum", "Bishop Arts", "Oak Cliff",
      "Lakewood", "Lower Greenville", "Highland Park", "Plano", "Frisco",
      "Richardson", "Garland", "Mesquite", "Irving", "Las Colinas",
    ],
    es: {
      heroKicker: "Electricidad en Dallas",
      heroTitle: "Dallas, tu factura sin sorpresas.",
      heroSub: "Tarifa fija con Oncor como tu distribuidora. Sin contratos, sin fee de cancelación.",
      whyTitle: "¿Por qué Dallas elige Velcor?",
      whyBody: "Oncor cubre prácticamente todo el área metropolitana de Dallas-Fort Worth (DFW). Velcor te conecta a esa red con un plan de tarifa fija — sin sustos en julio, sin trampa en diciembre. Atención bilingüe desde Houston.",
      faqs: [
        { q: "¿Velcor cubre mi ZIP de Dallas?", a: "Sí — todos los códigos 752XX y 750XX dentro del área de Oncor. Verifica tu dirección al inscribirte." },
        { q: "¿Cuánto cuesta la luz en Dallas?", a: "El hogar típico de Dallas usa ~1,100 kWh/mes con factura ~$128. Velcor te da una tarifa fija desde el día 30." },
        { q: "¿Oncor envía factura aparte?", a: "No. Los cargos de Oncor ya vienen incluidos en tu factura Velcor. Una sola factura mensual." },
        { q: "¿Hay contrato de 12 o 24 meses?", a: "No. En Velcor cancelas cuando quieras. Sin penalidades, sin sorpresas." },
      ],
    },
    en: {
      heroKicker: "Dallas Electricity",
      heroTitle: "Dallas, no-surprise billing.",
      heroSub: "Fixed-rate plans with Oncor as your utility. No contracts, no cancellation fee.",
      whyTitle: "Why Dallas picks Velcor",
      whyBody: "Oncor covers nearly all of the Dallas-Fort Worth metroplex. Velcor connects you to that grid with a fixed-rate plan — no July sticker shock, no December trap. Bilingual support headquartered in Houston.",
      faqs: [
        { q: "Does Velcor cover my Dallas ZIP?", a: "Yes — every 752XX and 750XX ZIP inside the Oncor footprint. Verify your address at signup." },
        { q: "How much does electricity cost in Dallas?", a: "The typical Dallas home uses ~1,100 kWh/month at ~$128. Velcor gives you a fixed rate from day 30." },
        { q: "Does Oncor bill separately?", a: "No. Oncor charges are already included in your Velcor bill. One monthly bill." },
        { q: "Is there a 12 or 24-month contract?", a: "No. With Velcor you cancel any time. No penalties, no surprises." },
      ],
    },
  },

  "san-antonio": {
    slug: "san-antonio",
    name: "San Antonio",
    state: "TX",
    population: "1.5M",
    tdu: { name: "AEP Texas Central", phone: "(866) 223-8508" },
    avgRate: 11.2,
    avgBill: 124,
    zips: [
      "78201", "78202", "78203", "78204", "78205", "78207", "78208", "78209",
      "78210", "78211", "78212", "78213", "78214", "78215", "78216", "78217",
      "78218", "78219", "78220", "78221", "78222", "78223", "78224", "78225",
      "78226", "78227", "78228", "78229", "78230", "78231", "78232", "78233",
      "78234", "78235", "78236", "78237", "78238", "78239", "78240", "78242",
      "78243", "78244", "78245", "78247", "78248", "78249", "78250", "78251",
      "78252", "78253", "78254", "78255", "78256", "78257", "78258", "78259",
      "78260", "78261", "78263", "78264", "78266",
    ],
    neighborhoods: [
      "Downtown San Antonio", "King William", "Southtown", "Alamo Heights",
      "Stone Oak", "Medical Center", "Westover Hills", "Helotes",
      "Boerne", "Universal City", "Schertz", "New Braunfels",
    ],
    es: {
      heroKicker: "Electricidad en San Antonio",
      heroTitle: "San Antonio, energía con tarifa fija.",
      heroSub: "Plan único con AEP Texas Central. Activación rápida y soporte 100% bilingüe.",
      whyTitle: "¿Por qué San Antonio elige Velcor?",
      whyBody: "AEP Texas Central distribuye electricidad en gran parte del área de San Antonio fuera de los límites de CPS Energy. Velcor te ofrece un plan de tarifa fija sobre esa red — predecible, transparente y sin contratos a largo plazo.",
      faqs: [
        { q: "¿Mi ZIP de San Antonio está dentro de AEP?", a: "Verifica tu dirección — gran parte de 782XX está cubierto por AEP Texas Central. Si caes dentro de CPS Energy (la utility municipal de SA), no podemos atenderte por regulación estatal." },
        { q: "¿Cuánto se paga de luz en San Antonio?", a: "El consumo promedio es ~1,080 kWh/mes con factura ~$124. Con Velcor tu tarifa queda fija." },
        { q: "¿Hay diferencia entre AEP y CPS Energy?", a: "Sí — CPS Energy es una utility municipal (no permite REPs). AEP Texas Central es una TDU privada que sí permite elegir tu REP. Velcor sólo opera en zonas AEP." },
        { q: "¿Velcor tiene oficina en San Antonio?", a: "Operamos 100% remoto desde Houston, con soporte telefónico bilingüe a (800) 715-6516 y WhatsApp." },
      ],
    },
    en: {
      heroKicker: "San Antonio Electricity",
      heroTitle: "San Antonio, fixed-rate energy.",
      heroSub: "One plan with AEP Texas Central. Fast activation and 100% bilingual support.",
      whyTitle: "Why San Antonio picks Velcor",
      whyBody: "AEP Texas Central delivers electricity to much of the San Antonio area outside the CPS Energy footprint. Velcor offers a fixed-rate plan on that grid — predictable, transparent, no long-term contracts.",
      faqs: [
        { q: "Is my San Antonio ZIP inside AEP?", a: "Verify your address — most 782XX is covered by AEP Texas Central. If you fall inside CPS Energy (the SA municipal utility), state regulation prevents us from serving you." },
        { q: "How much does electricity cost in San Antonio?", a: "Average consumption is ~1,080 kWh/month at ~$124. Velcor locks your rate." },
        { q: "What's the difference between AEP and CPS Energy?", a: "CPS Energy is a municipal utility (does not allow REP choice). AEP Texas Central is a private TDU that does allow you to pick your REP. Velcor only operates in AEP territories." },
        { q: "Does Velcor have a San Antonio office?", a: "We operate fully remote from Houston, with bilingual phone support at (800) 715-6516 and WhatsApp." },
      ],
    },
  },

  "fort-worth": {
    slug: "fort-worth",
    name: "Fort Worth",
    state: "TX",
    population: "950K",
    tdu: { name: "Oncor Electric Delivery", phone: "(888) 313-4747" },
    avgRate: 11.6,
    avgBill: 130,
    zips: [
      "76101", "76102", "76103", "76104", "76105", "76106", "76107", "76108",
      "76109", "76110", "76111", "76112", "76114", "76115", "76116", "76118",
      "76119", "76120", "76121", "76122", "76123", "76124", "76126", "76127",
      "76129", "76130", "76131", "76132", "76133", "76134", "76135", "76136",
      "76137", "76140", "76147", "76148", "76150", "76155", "76161", "76164",
      "76177", "76179", "76180", "76182", "76185", "76244", "76248",
    ],
    neighborhoods: [
      "Downtown Fort Worth", "Sundance Square", "West 7th", "TCU / Berry Street",
      "Magnolia Avenue", "Stockyards", "Cultural District", "Arlington Heights",
      "Ridglea Hills", "Keller", "Southlake", "Grapevine", "Arlington",
    ],
    es: {
      heroKicker: "Electricidad en Fort Worth",
      heroTitle: "Fort Worth merece simplicidad.",
      heroSub: "Tarifa fija con Oncor, sin contratos largos, atención bilingüe.",
      whyTitle: "¿Por qué Fort Worth elige Velcor?",
      whyBody: "Fort Worth comparte la red Oncor con Dallas, lo que te da acceso a una de las distribuidoras más confiables de Texas. Velcor te suma un REP transparente: tarifa fija, sin penalidades, soporte en español.",
      faqs: [
        { q: "¿Velcor cubre mi ZIP de Fort Worth?", a: "Sí — todos los códigos 761XX y 762XX dentro del área de Oncor. Solo verifica tu dirección al inscribirte." },
        { q: "¿Cuánto cuesta la luz en Fort Worth?", a: "El consumo promedio es ~1,120 kWh/mes con factura ~$130. Con Velcor queda fija desde el día 30." },
        { q: "¿La factura incluye los cargos de Oncor?", a: "Sí. Una sola factura mensual incluye suministro Velcor + entrega Oncor + impuestos PUCT." },
        { q: "¿Velcor opera en el lado de Arlington / Grapevine / Southlake?", a: "Sí — toda la zona DFW dentro del territorio Oncor. Verifica al inscribirte." },
      ],
    },
    en: {
      heroKicker: "Fort Worth Electricity",
      heroTitle: "Fort Worth deserves simplicity.",
      heroSub: "Fixed-rate plans with Oncor, no long contracts, bilingual support.",
      whyTitle: "Why Fort Worth picks Velcor",
      whyBody: "Fort Worth shares the Oncor grid with Dallas, giving you access to one of the most reliable utilities in Texas. Velcor layers a transparent REP on top: fixed rate, no penalties, Spanish-language support.",
      faqs: [
        { q: "Does Velcor cover my Fort Worth ZIP?", a: "Yes — every 761XX and 762XX ZIP inside the Oncor footprint. Just verify your address at signup." },
        { q: "How much does electricity cost in Fort Worth?", a: "Average use is ~1,120 kWh/month at ~$130. With Velcor it's locked from day 30." },
        { q: "Does the bill include Oncor charges?", a: "Yes. A single monthly bill includes Velcor supply + Oncor delivery + PUCT taxes." },
        { q: "Does Velcor operate in Arlington / Grapevine / Southlake?", a: "Yes — anywhere in the DFW Oncor footprint. Verify at signup." },
      ],
    },
  },

  arlington: {
    slug: "arlington",
    name: "Arlington",
    state: "TX",
    population: "395K",
    tdu: { name: "Oncor Electric Delivery", phone: "(888) 313-4747" },
    avgRate: 11.6,
    avgBill: 129,
    zips: [
      "76001", "76002", "76003", "76004", "76005", "76006", "76010", "76011",
      "76012", "76013", "76014", "76015", "76016", "76017", "76018", "76019",
      "76040", "76060", "76063", "76094", "76096",
    ],
    neighborhoods: [
      "Downtown Arlington", "Entertainment District", "UTA", "AT&T Stadium",
      "Pantego", "Dalworthington Gardens", "Grand Prairie", "Mansfield",
    ],
    es: {
      heroKicker: "Electricidad en Arlington",
      heroTitle: "Arlington, tu luz a precio fijo.",
      heroSub: "Plan único con Oncor, sin contratos largos, atención 100% bilingüe.",
      whyTitle: "¿Por qué Arlington elige Velcor?",
      whyBody: "Arlington está dentro de la red de Oncor, la distribuidora más grande de Texas. Con Velcor añades un REP con precio fijo, sin fee de cancelación, y atención en español desde Houston.",
      faqs: [
        { q: "¿Velcor cubre mi ZIP de Arlington?", a: "Sí — todos los 760XX dentro del área Oncor. Solo verifica tu dirección al inscribirte." },
        { q: "¿Cuánto se paga de luz en Arlington?", a: "Consumo promedio ~1,110 kWh/mes con factura ~$129. Velcor te deja la tarifa fija desde el día 30." },
        { q: "¿Atienden clientes en Grand Prairie o Mansfield?", a: "Sí — toda la zona DFW dentro del territorio Oncor." },
        { q: "¿Hay fee por cancelación anticipada?", a: "No. Cancelas cuando quieras, sin penalidades." },
      ],
    },
    en: {
      heroKicker: "Arlington Electricity",
      heroTitle: "Arlington, fixed-price power.",
      heroSub: "One plan with Oncor, no long contracts, 100% bilingual support.",
      whyTitle: "Why Arlington picks Velcor",
      whyBody: "Arlington sits inside Oncor's footprint, the largest TDU in Texas. Velcor adds a fixed-price REP on top — no cancellation fees and Spanish-language support from Houston.",
      faqs: [
        { q: "Does Velcor cover my Arlington ZIP?", a: "Yes — all 760XX codes inside the Oncor footprint. Just verify your address at signup." },
        { q: "How much does electricity cost in Arlington?", a: "Average use is ~1,110 kWh/month at ~$129. Velcor locks the rate from day 30." },
        { q: "Do you serve Grand Prairie or Mansfield?", a: "Yes — the entire DFW area within Oncor territory." },
        { q: "Is there an early-termination fee?", a: "No. Cancel any time without penalties." },
      ],
    },
  },

  "corpus-christi": {
    slug: "corpus-christi",
    name: "Corpus Christi",
    state: "TX",
    population: "320K",
    tdu: { name: "AEP Texas Central", phone: "(866) 223-8508" },
    avgRate: 11.3,
    avgBill: 126,
    zips: [
      "78401", "78402", "78403", "78404", "78405", "78406", "78407", "78408",
      "78409", "78410", "78411", "78412", "78413", "78414", "78415", "78416",
      "78417", "78418", "78419", "78426",
    ],
    neighborhoods: [
      "Downtown", "North Beach", "Flour Bluff", "Calallen", "Padre Island",
      "Portland", "Ingleside", "Aransas Pass", "Rockport",
    ],
    es: {
      heroKicker: "Electricidad en Corpus Christi",
      heroTitle: "Corpus Christi, energía predecible.",
      heroSub: "Tarifa fija con AEP Texas Central. Sin depósitos, sin sorpresas.",
      whyTitle: "¿Por qué Corpus Christi elige Velcor?",
      whyBody: "AEP Texas Central distribuye electricidad en todo el sur costero de Texas. Con Velcor añades un REP honesto, con tarifa fija y atención bilingüe — importante en una zona donde el calor y la humedad disparan el uso del A/C en verano.",
      faqs: [
        { q: "¿Velcor atiende ZIPs 784XX?", a: "Sí — toda la zona metropolitana de Corpus Christi bajo AEP Texas Central." },
        { q: "¿Cuánto se paga de luz en Corpus Christi?", a: "Consumo promedio ~1,090 kWh/mes con factura ~$126 gracias al calor costero." },
        { q: "¿Cubren las islas Padre y Rockport?", a: "Sí — donde AEP Texas Central sea la TDU. Verifica al inscribirte." },
        { q: "¿Aplican tarifas especiales para hogares con A/C intensivo?", a: "No — nuestro plan es tarifa fija por kWh, sin recargo por consumo alto." },
      ],
    },
    en: {
      heroKicker: "Corpus Christi Electricity",
      heroTitle: "Corpus Christi, predictable energy.",
      heroSub: "Fixed rate with AEP Texas Central. No deposits, no surprises.",
      whyTitle: "Why Corpus Christi picks Velcor",
      whyBody: "AEP Texas Central delivers power across coastal south Texas. Velcor adds an honest REP with a fixed rate and bilingual support — important in an area where heat and humidity push A/C usage through the summer.",
      faqs: [
        { q: "Does Velcor serve 784XX ZIPs?", a: "Yes — the entire Corpus Christi metro under AEP Texas Central." },
        { q: "How much does power cost in Corpus Christi?", a: "Average ~1,090 kWh/month at ~$126 due to coastal heat." },
        { q: "Do you cover Padre Island and Rockport?", a: "Yes — anywhere AEP Texas Central is the TDU. Verify at signup." },
        { q: "Are there surcharges for high-A/C homes?", a: "No — our plan is a flat per-kWh rate with no heavy-usage penalty." },
      ],
    },
  },

  galveston: {
    slug: "galveston",
    name: "Galveston",
    state: "TX",
    population: "53K",
    tdu: { name: "TNMP (Texas-New Mexico Power)", phone: "(888) 866-7456" },
    avgRate: 11.9,
    avgBill: 134,
    zips: [
      "77550", "77551", "77552", "77553", "77554", "77555", "77563",
      "77565", "77568", "77573", "77590", "77591", "77598",
    ],
    neighborhoods: [
      "East End Historic District", "The Strand", "Seawall", "Pirates Beach",
      "Jamaica Beach", "Tiki Island", "Bolivar Peninsula", "League City",
    ],
    es: {
      heroKicker: "Electricidad en Galveston",
      heroTitle: "Galveston, luz costera sin drama.",
      heroSub: "Tarifa fija con TNMP. Ideal para residentes y propietarios de casas de playa.",
      whyTitle: "¿Por qué Galveston elige Velcor?",
      whyBody: "Galveston Island y la zona costera están en el territorio de TNMP (Texas-New Mexico Power). Con Velcor obtienes un plan único con tarifa fija — perfecto si tienes una propiedad de verano o rentas de playa con consumo variable.",
      faqs: [
        { q: "¿Velcor cubre mi ZIP de Galveston?", a: "Sí — todos los 775XX dentro del área TNMP. Verifica tu dirección al inscribirte." },
        { q: "¿Puedo usar Velcor para una casa de playa de renta?", a: "Sí — la cuenta se abre a tu nombre y puedes activarla/desactivarla sin contrato de largo plazo." },
        { q: "¿La luz es más cara en Galveston por ser costa?", a: "TNMP cobra un poco más que Oncor por los costos de mantenimiento costero. Aún así, Velcor te da una tarifa fija competitiva." },
        { q: "¿Cubren la península de Bolivar?", a: "Sí — donde TNMP tenga su red. Verifica al inscribirte." },
      ],
    },
    en: {
      heroKicker: "Galveston Electricity",
      heroTitle: "Galveston, coastal power without drama.",
      heroSub: "Fixed rate with TNMP. Perfect for residents and beach-house owners.",
      whyTitle: "Why Galveston picks Velcor",
      whyBody: "Galveston Island and the coastal strip sit in TNMP's (Texas-New Mexico Power) territory. Velcor delivers a single fixed-rate plan — ideal if you own a vacation home or manage short-term rentals with variable consumption.",
      faqs: [
        { q: "Does Velcor cover my Galveston ZIP?", a: "Yes — all 775XX codes inside the TNMP footprint. Verify at signup." },
        { q: "Can I use Velcor for a rental beach house?", a: "Yes — the account is under your name and you can activate/deactivate without long-term contracts." },
        { q: "Is electricity more expensive in Galveston because of the coast?", a: "TNMP charges slightly more than Oncor due to coastal maintenance costs. Even so, Velcor gives you a competitive fixed rate." },
        { q: "Do you serve the Bolivar Peninsula?", a: "Yes — anywhere TNMP maintains its grid. Verify at signup." },
      ],
    },
  },

  waco: {
    slug: "waco",
    name: "Waco",
    state: "TX",
    population: "140K",
    tdu: { name: "Oncor Electric Delivery", phone: "(888) 313-4747" },
    avgRate: 11.5,
    avgBill: 125,
    zips: [
      "76701", "76702", "76703", "76704", "76705", "76706", "76707", "76708",
      "76710", "76711", "76712", "76714", "76716", "76797", "76798", "76799",
      "76633", "76638", "76657", "76664",
    ],
    neighborhoods: [
      "Downtown Waco", "Cameron Park", "Baylor University", "Sanger Heights",
      "Brook Oaks", "China Spring", "Hewitt", "Woodway", "Robinson", "Lorena",
    ],
    es: {
      heroKicker: "Electricidad en Waco",
      heroTitle: "Waco, tu cuenta de luz bajo control.",
      heroSub: "Plan único con Oncor. Tarifa fija desde el día 30, sin contratos a largo plazo.",
      whyTitle: "¿Por qué Waco elige Velcor?",
      whyBody: "Waco comparte la red de Oncor con el norte de Texas. Velcor te da un plan fijo con una sola factura mensual y atención bilingüe — simple, sin letra chica.",
      faqs: [
        { q: "¿Velcor cubre mi ZIP de Waco?", a: "Sí — todos los 767XX dentro del área Oncor. Solo verifica tu dirección." },
        { q: "¿Cuánto se paga de luz en Waco?", a: "Consumo promedio ~1,080 kWh/mes con factura ~$125. Velcor deja la tarifa fija." },
        { q: "¿Cubren Hewitt, Woodway y China Spring?", a: "Sí — toda el área metro bajo Oncor." },
        { q: "¿Hay diferencia entre Velcor y los proveedores locales?", a: "Sí — somos un REP bilingüe con una sola factura y cero fee de cancelación." },
      ],
    },
    en: {
      heroKicker: "Waco Electricity",
      heroTitle: "Waco, your electricity bill under control.",
      heroSub: "One plan with Oncor. Fixed rate from day 30, no long-term contracts.",
      whyTitle: "Why Waco picks Velcor",
      whyBody: "Waco shares the Oncor grid with north Texas. Velcor gives you a fixed plan with a single monthly bill and bilingual support — simple, no fine print.",
      faqs: [
        { q: "Does Velcor cover my Waco ZIP?", a: "Yes — all 767XX codes inside the Oncor footprint. Just verify your address." },
        { q: "How much does electricity cost in Waco?", a: "Average use is ~1,080 kWh/month at ~$125. Velcor locks your rate." },
        { q: "Do you serve Hewitt, Woodway and China Spring?", a: "Yes — the entire metro area under Oncor." },
        { q: "How is Velcor different from local providers?", a: "We're a bilingual REP with a single bill and zero cancellation fee." },
      ],
    },
  },
};

export const CITY_SLUGS = Object.keys(CITIES);
